import { useCallback, useMemo } from "react";
import type { FareDetailsV2 } from "@b2bportal/air-shopping-api";
import type {
  HtsAirCfarOffer,
  IRestrictionProps,
  Prices,
  TransformRestrictionsFn,
} from "@b2bportal/core-types";
import DOMPurify from "dompurify";

import { extractCfarMerchandisingRestrictions } from "../../utils";

const TITLE_TEXT_KEY = "title_text";
const TITLE_TEXT_KEY_FALLBACK = "title";

export interface UseFlightCfarFareViewParams {
  fareDetails: FareDetailsV2;
  sliceIndex: number;
  cfarOffer: HtsAirCfarOffer;
}

export interface UseFlightCfarFareViewResult {
  prices: Prices | undefined;
  brandName: string;
  refundabilityLabel: string;
  transformRestrictions: TransformRestrictionsFn;
  cfarOnlyRestrictions: IRestrictionProps[];
}

export const useFlightCfarFareView = ({
  fareDetails,
  sliceIndex,
  cfarOffer,
}: UseFlightCfarFareViewParams): UseFlightCfarFareViewResult => {
  const prices = useMemo(
    () => fareDetails.paxPricings?.[0]?.pricing.total,
    [fareDetails]
  );

  const brandName = fareDetails.slices[sliceIndex]?.fareShelf?.brandName ?? "";

  const refundabilityLabel = useMemo(() => {
    const raw =
      cfarOffer.merchandisingContents?.[TITLE_TEXT_KEY] ??
      cfarOffer.merchandisingContents?.[TITLE_TEXT_KEY_FALLBACK] ??
      "";
    return DOMPurify.sanitize(raw);
  }, [cfarOffer.merchandisingContents]);

  const cfarOnlyRestrictions = useMemo(
    () => extractCfarMerchandisingRestrictions(cfarOffer.merchandisingContents),
    [cfarOffer.merchandisingContents]
  );

  // Build a transform that prepends CFAR restriction bullets from merchandisingContents
  const transformRestrictions: TransformRestrictionsFn = useCallback(
    (baseRestrictions) => {
      return [...cfarOnlyRestrictions, ...baseRestrictions];
    },
    [cfarOnlyRestrictions]
  );

  return {
    prices,
    brandName,
    refundabilityLabel,
    transformRestrictions,
    cfarOnlyRestrictions,
  };
};
